// src/pages/guia/GuiaPanel.js
//
// Dashboard del Guía: métricas rápidas de sus afiliados, comisiones
// acumuladas, del mes actual, pagos recibidos y pendientes.

import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Grid, Card, CardContent, Box, Stack,
  LinearProgress, Alert, Chip, Divider,
} from '@mui/material';
import StorefrontIcon    from '@mui/icons-material/Storefront';
import RequestQuoteIcon  from '@mui/icons-material/RequestQuote';
import PaidIcon          from '@mui/icons-material/Paid';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { guia as api } from '../../api/guiaApi';
import { useApp } from '../../context/AppContext';

const fmtCOP = (n) => new Intl.NumberFormat('es-CO', {
  style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);

function Stat({ icon, label, value, color, sub }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
          <Box sx={{ color, display: 'flex' }}>{icon}</Box>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
            {label}
          </Typography>
        </Stack>
        <Typography variant="h4" fontWeight={700} sx={{ color }}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

export default function GuiaPanel() {
  const { notificar } = useApp();
  const [datos, setDatos] = useState(null);
  const [perfil, setPerfil] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, r] = await Promise.all([api.perfil(), api.panel()]);
        setPerfil(p);
        setDatos(r);
      } catch (e) {
        notificar(e.mensajeUsuario || 'No se pudo cargar el panel.', 'error');
      } finally { setCargando(false); }
    })();
  }, [notificar]);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box mb={3}>
        <Typography variant="h4" fontFamily="Fraunces">
          Hola{perfil?.nombreCompleto ? `, ${perfil.nombreCompleto.split(' ')[0]}` : ''}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Este es un resumen de tu actividad como Guía HolaVeci.
        </Typography>
      </Box>

      {cargando && <LinearProgress />}

      {datos && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Stat icon={<StorefrontIcon />} color="primary.main"
                    label="Afiliados activos" value={datos.afiliadosActivos}
                    sub={`${datos.afiliados360} con paquete 360°`} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stat icon={<RequestQuoteIcon />} color="warning.main"
                    label="Comisión del mes" value={fmtCOP(datos.comisionMesActual)}
                    sub={`Al ${datos.porcentajeComision}%`} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stat icon={<PaidIcon />} color="success.main"
                    label="Total pagado" value={fmtCOP(datos.totalPagado)}
                    sub="Liquidaciones ya cobradas" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stat icon={<HourglassEmptyIcon />} color="text.secondary"
                    label="Pendiente por cobrar" value={fmtCOP(datos.totalPendiente)}
                    sub="Liquidaciones abiertas" />
            </Grid>
          </Grid>

          <Card>
            <CardContent>
              <Typography variant="h6" fontFamily="Fraunces" gutterBottom>
                Comisiones acumuladas totales
              </Typography>
              <Typography variant="h3" color="success.main" fontWeight={700}>
                {fmtCOP(datos.comisionAcumulada)}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                Suma de todas las facturas SaaS pagadas de tus afiliados, calculada al{' '}
                {datos.porcentajeComision}% sobre la base gravable.
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Alert severity="info" variant="outlined">
                Solo cuentan las facturas cuyo tendero <b>pagó</b> HolaVeci. Si un afiliado
                deja de pagar, esa mensualidad no genera comisión.
              </Alert>
            </CardContent>
          </Card>
        </>
      )}
    </Container>
  );
}
